import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  ArrowUpRight,
  ArrowDownLeft,
  Repeat,
  ExternalLink,
  Filter,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
} from 'lucide-react';
import { WalletTransaction, SUPPORTED_CHAINS } from '../../types/multi-wallet';

export function MultiWalletActivity() {
  const [filter, setFilter] = useState<'all' | 'send' | 'receive' | 'swap'>('all');

  // Mock transactions - will be replaced with real aggregated transactions
  const transactions: WalletTransaction[] = [
    {
      id: '1',
      walletId: '1',
      walletAddress: '0x1234...5678',
      walletLabel: 'Main Wallet',
      hash: '0xabcd1234efgh5678ijkl9012mnop3456qrst7890uvwx1234yzab5678cdef9012',
      timestamp: Date.now() - 15 * 60 * 1000,
      type: 'swap',
      status: 'confirmed',
      token: { symbol: 'ETH → USDC', amount: '2.5 ETH → 5,612.50 USDC', valueUSD: 5612.50 },
      gasFee: '0.003 ETH',
      chainId: 1,
    },
    {
      id: '2',
      walletId: '2',
      walletAddress: '0xabcd...1234',
      walletLabel: 'Trading Wallet',
      hash: '0x5678abcd1234efgh5678ijkl9012mnop3456qrst7890uvwx1234yzab5678cdef',
      timestamp: Date.now() - 45 * 60 * 1000,
      type: 'receive',
      status: 'confirmed',
      token: { symbol: 'NDH', amount: '5,000', valueUSD: 4250.00 },
      from: '0x9876...5432',
      gasFee: '0.001 ETH',
      chainId: 1,
    },
    {
      id: '3',
      walletId: '1',
      walletAddress: '0x1234...5678',
      walletLabel: 'Main Wallet',
      hash: '0x1234567890abcdef1234567890abcdef1234567890abcdef1234567890abcdef',
      timestamp: Date.now() - 2 * 60 * 60 * 1000,
      type: 'send',
      status: 'confirmed',
      token: { symbol: 'USDC', amount: '1,000', valueUSD: 1000.00 },
      to: '0xfedc...ba98',
      gasFee: '0.002 ETH',
      chainId: 1,
    },
    {
      id: '4',
      walletId: '4',
      walletAddress: '0xfedc...ba98',
      walletLabel: 'DeFi Farming',
      hash: '0xfedcba9876543210fedcba9876543210fedcba9876543210fedcba9876543210',
      timestamp: Date.now() - 5 * 60 * 1000,
      type: 'stake',
      status: 'pending',
      token: { symbol: 'NDH', amount: '10,000', valueUSD: 8500.00 },
      gasFee: '0.005 MATIC',
      chainId: 137,
    },
    {
      id: '5',
      walletId: '3',
      walletAddress: '0x9876...5432',
      walletLabel: 'Cold Storage',
      hash: '0x0987654321fedcba0987654321fedcba0987654321fedcba0987654321fedcba',
      timestamp: Date.now() - 24 * 60 * 60 * 1000,
      type: 'receive',
      status: 'confirmed',
      token: { symbol: 'WBTC', amount: '0.5', valueUSD: 21625.00 },
      from: 'Coinbase',
      gasFee: '0.001 ETH',
      chainId: 1,
    },
    {
      id: '6',
      walletId: '2',
      walletAddress: '0xabcd...1234',
      walletLabel: 'Trading Wallet',
      hash: '0xaaaa1234bbbb5678cccc9012dddd3456eeee7890ffff1234aaaa5678bbbb9012',
      timestamp: Date.now() - 3 * 60 * 60 * 1000,
      type: 'swap',
      status: 'failed',
      token: { symbol: 'LINK → ETH', amount: '100 LINK → 0.62 ETH', valueUSD: 1425.00 },
      gasFee: '0.002 ETH',
      chainId: 42161,
    },
  ];

  const filteredTransactions = transactions.filter((tx) => {
    if (filter === 'all') return true;
    return tx.type === filter;
  });

  const formatTimeAgo = (timestamp: number) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / (60 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000));

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'send':
        return <ArrowUpRight className="h-5 w-5 text-red-500" />;
      case 'receive':
        return <ArrowDownLeft className="h-5 w-5 text-green-500" />;
      case 'swap':
        return <Repeat className="h-5 w-5 text-blue-500" />;
      case 'stake':
        return <ArrowUpRight className="h-5 w-5 text-violet-500" />;
      case 'unstake':
        return <ArrowDownLeft className="h-5 w-5 text-amber-500" />;
      default:
        return <ArrowUpRight className="h-5 w-5 text-slate-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
            <CheckCircle className="h-3 w-3 mr-1" />
            Confirmado
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Pendiente
          </Badge>
        );
      case 'failed':
        return (
          <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
            <XCircle className="h-3 w-3 mr-1" />
            Fallido
          </Badge>
        );
      default:
        return null;
    }
  };

  const truncateHash = (hash: string) => {
    return `${hash.slice(0, 10)}...${hash.slice(-8)}`;
  };

  return (
    <div className="space-y-4">
      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-slate-500" />
        <div className="flex gap-2">
          {(['all', 'send', 'receive', 'swap'] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'outline'}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'Todas' : f === 'send' ? 'Enviadas' : f === 'receive' ? 'Recibidas' : 'Swaps'}
            </Button>
          ))}
        </div>
      </div>

      {/* Transactions List */}
      <Card>
        <CardHeader>
          <CardTitle>Actividad Reciente</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-8 text-slate-600 dark:text-slate-400">
              No hay transacciones que mostrar
            </div>
          ) : (
            <div className="space-y-4">
              {filteredTransactions.map((tx) => {
                const chainInfo = SUPPORTED_CHAINS[tx.chainId];
                return (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        tx.type === 'send' ? 'bg-red-500/20' :
                        tx.type === 'receive' ? 'bg-green-500/20' :
                        tx.type === 'swap' ? 'bg-blue-500/20' :
                        'bg-violet-500/20'
                      }`}>
                        {getTypeIcon(tx.type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {tx.token.symbol}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {tx.walletLabel}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="text-xs"
                            style={{ borderColor: chainInfo?.color, color: chainInfo?.color }}
                          >
                            {chainInfo?.name}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          {tx.token.amount}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                          <Clock className="h-3 w-3" />
                          {formatTimeAgo(tx.timestamp)}
                          <span>·</span>
                          <code className="font-mono">{truncateHash(tx.hash)}</code>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-medium text-slate-900 dark:text-slate-100">
                          ${tx.token.valueUSD.toLocaleString()}
                        </p>
                        <p className="text-xs text-slate-500">
                          Gas: {tx.gasFee}
                        </p>
                      </div>

                      {getStatusBadge(tx.status)}

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          const explorer = tx.chainId === 1 ? 'etherscan.io' :
                                          tx.chainId === 137 ? 'polygonscan.com' :
                                          tx.chainId === 42161 ? 'arbiscan.io' : 'etherscan.io';
                          window.open(`https://${explorer}/tx/${tx.hash}`, '_blank');
                        }}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
